// Pure scale-selection for the range slider (./rangeControl.ts). DOM-free and
// dependency-free — picks linear/log/symlog from a field's min/max alone (no
// distribution scan, so it stays cheap) and sets the symlog linear band to the
// innermost power of ten near 0. Unit-tested in isolation; lifts unchanged into
// webpic's @webpic/ui/controls.

import type { ScaleKind } from './rangeMath';

/** Decades of log tail to show above the linear band: the symlog half-width is
 *  the power of ten this many decades below the field's peak. 3 ⇒ a ±45000 field
 *  (10^4.65) gets linthresh 10^(4-3) = 10 — the innermost decade tick near 0. */
const TAIL_DECADES = 3;
/** A strictly-positive field whose max/min ratio is at least this wide gets log;
 *  below it, linear. */
const MIN_LOG_DECADES = 2;
/** Beyond this, a strictly-positive field is treated as having a near-zero floor
 *  (→ symlog, since pure log can't render values that small relative to the peak). */
const MAX_LOG_DECADES = 6;

/** Nearest "nice" 1/2/5×10ⁿ value ≥ 0 (0 for non-positive / non-finite input).
 *  Used for slider drag-step sizing (the symlog linthresh is an exact power of
 *  ten via {@link chooseFieldScale}, so it doesn't need rounding). */
export function niceMagnitude(x: number): number {
    if (!(x > 0) || !Number.isFinite(x)) return 0;
    const base = 10 ** Math.floor(Math.log10(x));
    const f = x / base;
    return (f < 1.5 ? 1 : f < 3.5 ? 2 : f < 7.5 ? 5 : 10) * base;
}

export interface FieldScaleStats {
    /** Signed field minimum. */
    readonly min: number;
    /** Signed field maximum. */
    readonly max: number;
    /** max(|min|, |max|). */
    readonly maxAbs: number;
}

export interface FieldScale {
    readonly kind: ScaleKind;
    /** symlog linear half-width (a power of ten); 0 for linear/log. */
    readonly linthresh: number;
    /** Single-mode fill anchor: 0 for symlog, else min. */
    readonly origin: number;
}

/** The innermost power of ten near 0 — `TAIL_DECADES` below the field's peak
 *  decade, so the symlog tail shows ~3 decade ticks above it. ±45000 → 10. */
function innermostDecade(maxAbs: number): number {
    return 10 ** (Math.floor(Math.log10(maxAbs)) - TAIL_DECADES);
}

/** Choose a slider scale from a field's range alone (no distribution scan).
 *
 *  A strictly-positive field uses its honest dynamic range `log10(max/min)`:
 *  linear when narrow, log when wide and clean. Everything else (crosses zero,
 *  strictly negative, or positive with a near-zero minimum) is `symlog`, with the
 *  linear band pinned to the innermost power of ten near 0 — fine control around
 *  zero, the high-magnitude tail log-compressed. */
export function chooseFieldScale(stats: FieldScaleStats): FieldScale {
    const { min, max, maxAbs } = stats;
    if (!(maxAbs > 0)) return { kind: 'linear', linthresh: 0, origin: min < 0 && max > 0 ? 0 : min };

    if (min > 0) {
        const decades = Math.log10(max / min);
        if (decades < MIN_LOG_DECADES) return { kind: 'linear', linthresh: 0, origin: min };
        if (decades <= MAX_LOG_DECADES) return { kind: 'log', linthresh: 0, origin: min };
        // near-zero floor falls through to symlog (log can't render it)
    }
    return { kind: 'symlog', linthresh: innermostDecade(maxAbs), origin: 0 };
}
